/**
 * CreateAccountPage Component
 * Public page for accepting invitation and creating account
 */

import { useState, useEffect } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import { usersService } from '../services/users.service';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { AuthenticatedSessionWarning } from '../components/auth/AuthenticatedSessionWarning';
import { useToast } from '../hooks/useToast';
import { validatePassword, getPasswordHelperText } from '../lib/passwordValidation';
import { handleApiError } from '../lib/axios';

interface InvitationDetails {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  member_id: number;
}

type InvitationError = 'invalid' | 'expired' | 'already_used' | 'revoked';

export const CreateAccountPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const setAuth = useAuthStore((state) => state.setAuth);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const token = searchParams.get('token');

  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);
  const [invitationError, setInvitationError] = useState<InvitationError | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setInvitationError('invalid');
      setIsVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await usersService.verifyInvitation(token);
        if (result.valid) {
          setInvitationDetails({
            email: result.email!,
            first_name: result.first_name!,
            last_name: result.last_name!,
            role: result.role!,
            member_id: result.member_id!,
          });
        } else {
          setInvitationError(result.error!);
        }
      } catch (error) {
        setInvitationError('invalid');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      usersService.acceptInvitation(data.token, data.password),
    onSuccess: (response) => {
      // Auto-login: save JWT and user data to auth store
      // Construct User object from response
      const user = {
        id: response.user.id,
        member_id: response.user.member_id,
        email: response.user.email,
        role: response.user.role,
        is_active: response.user.is_active,
        created_at: response.user.created_at,
        updated_at: response.user.updated_at,
      };

      // Construct minimal Member object for initial auth
      // Full member data will be loaded when needed
      const member = {
        id: response.user.member_id,
        first_name: response.user.first_name,
        last_name: response.user.last_name,
        email: response.user.email,
        gender: 'male' as const,
        date_of_birth: null,
        position: null,
        team_id: null,
        department_id: null,
        date_joined: response.user.created_at,
        biography: null,
        profile_picture: null,
        profile_picture_url: null,
        has_profile_picture: false,
        is_active: true,
        removed_at: null,
      };

      setAuth(user, member, response.token, response.tenant?.name);

      toast.success('Account created successfully! Welcome to Team Wizard.');

      // Redirect based on user role using hard redirect to avoid React Router interception
      const redirectPath = hasAdminAccess(response.user.role) ? '/dashboard' : '/home';
      window.location.href = redirectPath;
    },
    onError: (error: unknown) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    // Validate password
    const validation = validatePassword(password);
    if (!validation.valid) {
      setPasswordError(validation.error!);
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (!token) {
      toast.error('Invalid invitation token');
      return;
    }

    acceptMutation.mutate({ token, password });
  };

  const handleRequestNew = () => {
    navigate(`/request-new-invite?token=${token}`);
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <LoadingSpinner size="lg" text="Verifying invitation..." />
      </div>
    );
  }

  // Error states
  if (invitationError === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <div className="text-red-500 text-5xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-heading mb-2">Invalid Invitation</h1>
          <p className="text-meta mb-6">
            This invitation link is invalid or has been removed.
          </p>
          <Button onClick={() => navigate('/login')} variant="secondary">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (invitationError === 'revoked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <div className="text-orange-500 text-5xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-heading mb-2">Invitation Cancelled</h1>
          <p className="text-meta mb-6">
            This invitation has been cancelled by an administrator. Please contact your administrator for assistance.
          </p>
          <Button onClick={() => navigate('/login')} variant="secondary">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (invitationError === 'already_used') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <div className="text-green-500 text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-heading mb-2">Already Activated</h1>
          <p className="text-meta mb-6">
            This invitation has already been used. Please log in with your email and password.
          </p>
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (invitationError === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-app">
        <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <div className="text-orange-500 text-5xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold text-heading mb-2">Invitation Expired</h1>
          <p className="text-meta mb-6">
            This invitation link has expired. You can request a new one below.
          </p>
          <Button onClick={handleRequestNew}>
            Request New Invitation
          </Button>
        </div>
      </div>
    );
  }

  // Valid invitation - show account creation form
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-app">
      <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <img
              src="/team-wizard-logo-small.png"
              alt="Team Wizard"
              className="h-12 w-auto mx-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-heading mb-2">
            Welcome to Team Wizard!
          </h1>
          <p className="text-meta">
            Create your account to get started
          </p>
        </div>

        {isAuthenticated && (
          <AuthenticatedSessionWarning reason="accept this invitation" />
        )}

        <form
          onSubmit={handleSubmit}
          className={`space-y-6 ${isAuthenticated ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {invitationDetails && (
            <Input
              label="Email Address"
              type="email"
              value={invitationDetails.email}
              disabled
              readOnly
            />
          )}

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            helperText={getPasswordHelperText()}
          />

          <Input
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            required
          />

          {passwordError && (
            <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
          )}

          <Button
            type="submit"
            isLoading={acceptMutation.isPending}
            className="w-full"
          >
            Create Account
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={handleRequestNew}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Request a new invitation link
          </button>
        </div>
      </div>
    </div>
  );
};
